import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { conciergeAskStream, loadAuthUser, startGoogleLogin } from "../lib/api";
import { track } from "../lib/track";

// Pull every /tool/<slug> the assistant has linked across the thread, in order
// of first appearance, deduped. These are the tools we offer to assemble.
function extractRecommendedSlugs(msgs) {
  const slugs = [];
  const seen = new Set();
  const re = /\/tool\/([a-z0-9][a-z0-9-]*)/gi;
  for (const m of msgs) {
    if (m.role !== "assistant") continue;
    let mm;
    while ((mm = re.exec(m.content || "")) !== null) {
      const s = mm[1].toLowerCase();
      if (!seen.has(s)) {
        seen.add(s);
        slugs.push(s);
      }
    }
  }
  return slugs;
}

// Derive a concise terminal label from the user's search criteria (their first
// concierge query), stripping common filler so "tools for tracking SEC filings"
// becomes "tracking SEC filings". Original casing preserved (keeps "SEC").
// Returns "" when there's nothing useful to label with.
function deriveTerminalLabel(msgs) {
  const firstUser = msgs.find((m) => m.role === "user");
  let q = (firstUser?.content || "").trim();
  if (!q) return "";
  q = q
    .replace(/^(what|which)\s+tools?\s+(should i use\s+)?(to|for|that)\s+/i, "")
    .replace(/^tools?\s+(for|to|that)\s+/i, "")
    .replace(/^(find|show|get|give|recommend|suggest)\s+(me\s+)?(some\s+|the\s+|a\s+)?/i, "")
    .replace(/^(i\s+(need|want|am looking for)|looking for|help me( find)?)\s+(a\s+|some\s+|the\s+)?/i, "")
    .replace(/^(best|top|good)\s+/i, "")
    .replace(/[?".]+$/g, "")
    .trim();
  return q.split(/\s+/).slice(0, 6).join(" ").slice(0, 48).trim();
}

const STARTER_PROMPTS = [
  "tracking SEC filings",
  "alternatives to bloomberg terminal",
  "backtesting platforms under $50/mo",
  "screening tools for bank stocks",
  "best tools for a small-cap value focused hedge fund (<$10m aum)",
];

// Inline markdown parser — purposely minimal. We only support what the
// concierge system prompt instructs Claude to emit: paragraphs separated
// by blank lines and `[label](/tool/<slug>)` inline links to internal
// tool pages. Anything else is rendered as plain text. Keeps the
// brutalist aesthetic intact and avoids pulling in a markdown lib for
// ~5 patterns.
//
// Defensive rewrite: older sessions (saved before the path was fixed)
// have bare `/<slug>` links that pointed nowhere — those get rewritten
// to `/tool/<slug>` here so historical threads still navigate correctly.
const KNOWN_TOP_LEVEL_PATHS = new Set([
  "submit", "ask", "tickers", "about", "terms", "privacy", "admin",
  "stacks", "tags", "tools",
]);
function rewriteHref(href) {
  if (!href.startsWith("/")) return href;
  if (href.startsWith("/tool/")) return href;
  // Match exactly `/<slug>` with no extra path segments or query.
  const m = href.match(/^\/([a-z0-9][a-z0-9-]*)$/i);
  if (!m) return href;
  if (KNOWN_TOP_LEVEL_PATHS.has(m[1].toLowerCase())) return href;
  return `/tool/${m[1]}`;
}

function renderInline(text) {
  const out = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const label = m[1];
    const href = rewriteHref(m[2].trim());
    if (href.startsWith("/")) {
      out.push(
        <Link key={`l-${i++}`} to={href} className="tv-link">
          {label}
        </Link>,
      );
    } else if (/^https?:\/\//.test(href) || href.startsWith("mailto:")) {
      out.push(
        <a
          key={`l-${i++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="tv-link"
        >
          {label}
        </a>,
      );
    } else {
      // Unknown scheme — drop the link, keep the label.
      out.push(label);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function ReplyBlock({ text }) {
  // Split on blank lines → paragraphs. Single newlines collapse to a
  // space (matches Claude's typical prose output).
  const paragraphs = (text || "")
    .replace(/\r/g, "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <div className="text-[13px] leading-[1.55] text-black space-y-3" data-testid="concierge-reply">
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-wrap">
          {renderInline(p)}
        </p>
      ))}
    </div>
  );
}

/**
 * Concierge — chat UI for the AI tool advisor.
 *
 * Two render modes via `compact` prop:
 *  - compact (default): single-question hero box on Home.jsx
 *  - full: dedicated /ask page with persistent thread + follow-ups
 */
export default function Concierge({
  compact = false,
  initialSession = null,
  initialMessages = null,
  onSessionStarted,
  addMode = false,
  onAddTools = null,
  seedQuery = "",
  autoRun = false,
  onClear = null,
  surface = "web",
}) {
  const [query, setQuery] = useState(seedQuery || "");
  const [messages, setMessages] = useState(initialMessages || []);
  const [sessionId, setSessionId] = useState(initialSession);
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const didAutoRun = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    if (initialMessages) setMessages(initialMessages);
    if (initialSession) setSessionId(initialSession);
  }, [initialMessages, initialSession]);

  // Auto-fire the seeded question once (used when launched from the ⌘K palette
  // with a `?<question>` so the user doesn't have to press enter again).
  useEffect(() => {
    if (autoRun && !didAutoRun.current && (seedQuery || "").trim()) {
      didAutoRun.current = true;
      ask(seedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, seedQuery]);

  useEffect(() => {
    if (!compact && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, loading, compact]);

  // Stream a concierge answer for `apiMsg`, showing `displayMsg` as the user
  // turn. When offerTerminalAfter is set, attach a "build terminal" offer to
  // the finished reply, built from the tools it just linked.
  const streamAsk = async (apiMsg, { displayMsg, offerTerminalAfter = false } = {}) => {
    setError("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: displayMsg ?? apiMsg },
      { role: "assistant", content: "", streaming: true },
    ]);
    setQuery("");
    setLoading(true);

    const setLastAssistant = (updater) =>
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === "assistant") next[next.length - 1] = updater(last);
        return next;
      });

    try {
      const full = await conciergeAskStream(
        { session_id: sessionId, message: apiMsg, surface },
        {
          onMeta: ({ session_id }) => {
            if (session_id) {
              setSessionId(session_id);
              if (onSessionStarted) onSessionStarted(session_id);
            }
          },
          onChunk: (_delta, fullText) =>
            setLastAssistant((last) => ({ ...last, content: fullText })),
        },
      );
      setLastAssistant((last) => {
        const updated = { ...last, streaming: false };
        if (offerTerminalAfter) {
          const slugs = extractRecommendedSlugs([{ role: "assistant", content: full }]);
          if (slugs.length) updated.terminalOffer = { slugs };
        }
        return updated;
      });
    } catch (e) {
      const detail =
        e?.detail ||
        (e?.status === 429
          ? "daily limit reached — try again tomorrow"
          : "concierge unreachable — try again in a moment");
      setError(detail);
      // Drop the user msg + empty assistant placeholder so the thread isn't
      // left with a stale half-turn.
      setMessages((prev) => prev.slice(0, -2));
    } finally {
      setLoading(false);
    }
  };

  const ask = async (text) => {
    const msg = (text || "").trim();
    if (!msg || loading) return;
    setError("");
    track("concierge_ask", { q: msg, session_id: sessionId, surface: "concierge" });

    // In add-mode (inside a terminal) every answer offers to add the
    // recommended tools to the current terminal — no secret word needed.
    if (addMode) {
      await streamAsk(msg, { offerTerminalAfter: true });
      return;
    }

    await streamAsk(msg);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    ask(query);
  };

  // Build a stack from the tools recommended in this thread. Stash the intent
  // + slugs, then gate behind Google login (skip the redirect if signed in).
  const onBuild = async (action) => {
    const slugs = extractRecommendedSlugs(messages);
    if (!slugs.length) {
      toast.error("no tools recommended yet — ask for a few first");
      return;
    }
    const label = action === "human" ? deriveTerminalLabel(messages) : "";
    try {
      localStorage.setItem("tv_build_intent", JSON.stringify({ action, slugs, label }));
    } catch {
      /* storage blocked — non-fatal */
    }
    const user = await loadAuthUser();
    if (user) nav("/build");
    else startGoogleLogin("/build");
  };

  // Add-mode offer (inside a terminal): append the picks to the current terminal.
  const handleOffer = async (slugs) => {
    if (building || !slugs?.length || !onAddTools) return;
    setBuilding(true);
    try {
      await onAddTools(slugs);
    } finally {
      setBuilding(false);
    }
  };

  const showStarters = messages.length === 0 && !loading;
  // Suggestions live in a dropdown that opens when the ask box is focused
  // (and only before the first message / while not typing a fresh query).
  const showStarterDropdown = showStarters && focused && !query.trim();

  // Tools the concierge has recommended across this thread (non-addMode only).
  const recommended = !addMode ? extractRecommendedSlugs(messages) : [];

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setError("");
    setQuery("");
    if (onClear) onClear();
  };

  return (
    <div data-testid="concierge" className="w-full">
      {/* Clear / new chat */}
      {messages.length > 0 && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={clearChat}
            className="text-[11px] text-[#888] hover:text-black hover:underline"
            data-testid="concierge-clear"
          >
            [clear / new chat]
          </button>
        </div>
      )}
      {/* Thread */}
      {messages.length > 0 && (
        <div className="space-y-5 mb-4" data-testid="concierge-thread">
          {messages.map((m, i) => (
            <div key={i} data-testid={`concierge-msg-${m.role}`}>
              {m.role === "user" ? (
                <div className="text-[13px] text-[#666]">
                  <span className="text-black font-bold">{">"}</span>{" "}
                  <span className="text-black">{m.content}</span>
                </div>
              ) : (
                <>
                  <ReplyBlock text={m.content} />
                  {m.terminalOffer && (
                    <div
                      className="mt-3 border-2 border-black bg-[#FBF8EE] p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                      data-testid="terminal-offer-card"
                    >
                      <div className="text-[12px] text-[#333] leading-snug">
                        {addMode
                          ? `add ${m.terminalOffer.slugs.length} tool${
                              m.terminalOffer.slugs.length === 1 ? "" : "s"
                            } to your terminal?`
                          : "assemble these into a shareable personal terminal — a brutalist dashboard with a quick-launch popup."}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOffer(m.terminalOffer.slugs)}
                        disabled={building}
                        className="bg-black text-white px-3 py-2 text-[12px] font-bold whitespace-nowrap hover:bg-[#222] transition-colors self-start sm:self-auto"
                        data-testid="build-terminal-btn"
                      >
                        {building
                          ? addMode
                            ? "adding..."
                            : "building..."
                          : addMode
                            ? "[ add to terminal → ]"
                            : "[ build my terminal → ]"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {loading &&
            messages[messages.length - 1]?.role === "assistant" &&
            !messages[messages.length - 1]?.content && (
              <div className="text-[13px] text-[#888]" data-testid="concierge-loading">
                thinking<span className="tv-blink">_</span>
              </div>
            )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Build offer — appears once the concierge has recommended ≥1 tool,
          directly under the recommendations and ABOVE the follow-up input.
          "Human" saves a personal terminal (+ installable PWA); "Agent"
          exports a factual JSON/MD stack. Both gate behind Google login. */}
      {!addMode && !loading && recommended.length > 0 && (
        <div
          className="mb-4 border-2 border-black bg-[#FBF8EE] p-3"
          data-testid="build-offer"
        >
          <div className="text-[12px] text-[#333] font-bold mb-2">
            {">"}_ use these {recommended.length} recommended tool
            {recommended.length === 1 ? "" : "s"}:
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onBuild("human")}
              className="bg-black text-white px-3 py-2 text-[12px] font-bold whitespace-nowrap hover:bg-[#222] transition-colors"
              data-testid="build-for-human-btn"
            >
              [ save to my terminal → ]
            </button>
            <button
              type="button"
              onClick={() => onBuild("agent")}
              className="border border-black bg-white text-black px-3 py-2 text-[12px] font-bold whitespace-nowrap hover:bg-[#f3f3f3] transition-colors"
              data-testid="build-for-agent-btn"
            >
              [ Export Agent Toolkit → ]
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={onSubmit} className="flex flex-wrap items-stretch gap-2" data-testid="concierge-form">
        <div className="relative flex-1 min-w-0">
          <div className="flex items-center border border-black bg-white">
            <span className="px-2 text-[14px] font-bold select-none">{">"}</span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
              placeholder={
                messages.length === 0
                  ? "ask: what tool should i use to..."
                  : "ask a follow-up..."
              }
              disabled={loading}
              maxLength={1000}
              className="flex-1 min-w-0 px-1 py-2 font-mono text-[13px] bg-white text-black outline-none disabled:opacity-50"
              data-testid="concierge-input"
              autoComplete="off"
            />
          </div>
          {/* Starter-prompt dropdown — opens on focus */}
          {showStarterDropdown && (
            <div
              className="absolute left-0 right-0 top-full z-20 border border-t-0 border-black bg-white shadow-[2px_2px_0_0_#000]"
              data-testid="concierge-starters"
            >
              <div className="px-2 py-1 text-[11px] text-[#888] border-b border-[#eee] select-none">
                try one of these:
              </div>
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setFocused(false);
                    ask(p);
                  }}
                  className="block w-full text-left px-2 py-1.5 font-mono text-[12px] text-[#0645ad] hover:bg-[#FBF8EE]"
                  data-testid={`concierge-starter-${p.split(" ")[0]}`}
                >
                  {">"} {p}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="tv-btn tv-btn-primary"
          data-testid="concierge-submit"
        >
          {loading ? "..." : "ask"}
        </button>
      </form>

      {/* Starter prompts now live in the focus dropdown above. */}

      {error && (
        <div className="mt-3 text-[12px] text-[#c00]" data-testid="concierge-error">
          [error] {error}
        </div>
      )}

      {!compact && messages.length > 0 && (
        <div className="mt-4 text-[11px] text-[#888]">
          [the concierge only knows about tools in this index. it does not give financial advice.]
        </div>
      )}
    </div>
  );
}
